import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertTriangle, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useT } from '@/i18n';
import { householdApi, type HouseholdMemberView } from './household.api';
import { ApiError } from '@/lib/api-client';

/**
 * Cluster C — delete modal for a captured household_member.
 *
 * Hard delete with audit-trail preservation. household_members has no
 * `deleted_at` column (verified via psql on staging HEAD e51bf64). The
 * backend `DELETE /household-members/:id` route hits service.delete()
 * which emits `HOUSEHOLD_MEMBER_DELETED` audit BEFORE the DELETE so the
 * forensic snapshot is captured (status at delete + wasPromoted +
 * promotedClientId + reason + deletedByRole).
 *
 * Optional reason field for the audit metadata. Mandatory confirm click.
 */
export interface DeleteHouseholdMemberModalProps {
  member: HouseholdMemberView;
  screeningId: string;
  onClose: () => void;
  onDeleted?: () => void;
}

export function DeleteHouseholdMemberModal({
  member,
  screeningId,
  onClose,
  onDeleted,
}: DeleteHouseholdMemberModalProps): JSX.Element {
  const t = useT();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');

  const mut = useMutation({
    mutationFn: () => householdApi.delete(member.id, reason.trim() || undefined),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['screening', screeningId, 'household-members'],
      });
      void qc.invalidateQueries({ queryKey: ['household-members', member.id] });
      onDeleted?.();
      onClose();
    },
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !mut.isPending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, mut.isPending]);

  // B-02: createPortal a document.body, mismo patron de B-04 (EditClientInfoModal).
  // ScreeningPage lleva animate-fade-up-slow (fill-mode:both) en un ancestro,
  // el transform pegado del keyframe final crea containing block y rompe el
  // position:fixed del modal.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-hm-title"
      className="fixed inset-0 z-[60] flex items-center justify-center"
    >
      <div className="absolute inset-0 bg-nav-navy/40" onClick={() => !mut.isPending && onClose()} aria-hidden />
      <div className="relative bg-white rounded-lg shadow-elevated p-5 max-w-md w-[90%] z-10">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-nav-red" aria-hidden />
            <h3 id="delete-hm-title" className="text-base font-semibold text-nav-navy">
              {t('householdStep.delete.title' as never)}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={mut.isPending}
            className="p-1 rounded hover:bg-nav-soft"
            aria-label={t('householdStep.delete.cancelButton' as never)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-sm text-nav-navy mb-4">
          {t('householdStep.delete.body' as never, { name: member.fullName })}
        </p>

        <div className="space-y-3">
          <div>
            <Label htmlFor="delete-hm-reason">{t('householdStep.delete.reasonLabel' as never)}</Label>
            <Textarea
              id="delete-hm-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder=""
            />
          </div>

          {mut.error && (
            <Alert variant="error" role="alert">
              <AlertDescription>
                {mut.error instanceof ApiError ? mut.error.message : (mut.error as Error).message}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={mut.isPending}>
            {t('householdStep.delete.cancelButton' as never)}
          </Button>
          <Button size="sm" variant="danger" onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('householdStep.delete.confirmButton' as never)}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
